import { Button, makeStyles, mergeClasses, Tooltip } from '@fluentui/react-components';
import React, { RefObject, useCallback } from 'react';
import {
  ArrowSyncRegular,
  ClipboardArrowRightRegular,
  DocumentTextRegular,
} from '@fluentui/react-icons';
import { useExportReportMutation } from '../../queries/recording';

const useStyles = makeStyles({
  wrapperStyle: {
    display: 'flex',
    flex: 1,
    gap: '10px',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
});

export const MeetingNotesActionsBar = (props: {
  className?: string;
  recordingId: string;
  meetingNotesref: RefObject<HTMLDivElement>;
  globalInfosRef: RefObject<HTMLDivElement>;
  editable: boolean;
  transcriptExpired?: boolean;
  onRegenerateClick: () => void;
}) => {
  const {
    className,
    recordingId,
    meetingNotesref,
    globalInfosRef,
    editable,
    transcriptExpired,
    onRegenerateClick,
  } = props;
  const { mutateAsync: doExport, isPending: isExporting } =
    useExportReportMutation();

  const exportReport = useCallback(async () => {
    const file = await doExport(recordingId);
    const url = window.URL.createObjectURL(file);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${recordingId}.docx`);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  }, [doExport, recordingId]);

  const copyToClipboard = useCallback(async () => {
    if (globalInfosRef?.current && meetingNotesref?.current) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob(
            [
              globalInfosRef?.current.innerText,
              meetingNotesref?.current.innerText,
            ],
            {
              type: 'text/plain',
            }
          ),
          'text/html': new Blob(
            [
              globalInfosRef?.current.innerHTML,
              meetingNotesref?.current.innerHTML,
            ],
            {
              type: 'text/html',
            }
          ),
        }),
      ]);
    }
  }, [globalInfosRef, meetingNotesref]);

  const styles = useStyles();

  return (
    <div
      className={
        className
          ? mergeClasses(styles.wrapperStyle, className)
          : styles.wrapperStyle
      }
    >
      {editable &&
        (transcriptExpired ? (
          <Tooltip
            content="Transcription supprimée après 3 mois — régénération impossible. Le compte-rendu actuel reste disponible."
            relationship="label"
          >
            <Button
              appearance="secondary"
              aria-label="regenerate"
              icon={<ArrowSyncRegular />}
              disabled
            >
              Regénérer
            </Button>
          </Tooltip>
        ) : (
          <Button
            appearance="secondary"
            aria-label="regenerate"
            icon={<ArrowSyncRegular />}
            onClick={onRegenerateClick}
          >
            Regénérer
          </Button>
        ))}
      <Button
        appearance="secondary"
        aria-label="export"
        icon={<DocumentTextRegular />}
        onClick={exportReport}
        disabled={isExporting}
      >
        Exporter
      </Button>
      <Button
        appearance="secondary"
        aria-label="copy"
        icon={<ClipboardArrowRightRegular />}
        onClick={copyToClipboard}
      >
        Copier dans le presse-papier
      </Button>
    </div>
  );
};
